<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { fetchCompetenceObservations } from '@/api/resources/competenceObservation.api';
import { useReport } from '@/composables/report/useReport';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import ClassOverview from '@/core/dialogs/create-class/ClassOverview.vue';
import AnyIcon from '@/core/elements/AnyIcon.vue';
import FormTextarea from '@/core/forms/FormTextarea.vue';
import { formatNumber } from '@/utils/string.utils';
import { EIcon } from 'common/enum/misc.enum';
import { computed, ref, toRefs, watch } from 'vue';

const { getReports } = useReport();

const emit = defineEmits<{
  continue: [];
  'update:final-note': [note: string];
}>();

const props = withDefaults(
  defineProps<{
    schoolClass: Components.Schemas.SchoolClassDto | null;
    students: Components.Schemas.StudentDto[];
    showDescription?: boolean;
    allowFinalNote?: boolean;
    finalNote?: string;
  }>(),
  {
    showDescription: true,
    allowFinalNote: false,
    finalNote: '',
  },
);
const { schoolClass, finalNote } = toRefs(props);

const competenceObservations = ref<Components.Schemas.CompetenceObservationDto[]>([]);
const reportsNumber = ref<number>(0);
const isFinalNoteInputVisible = ref(false);
const finalNoteInternal = ref('');

const competenceObservationsAmountFormatted = computed<string>(() => {
  return formatNumber(competenceObservations.value.length);
});

const { tContainer } = useContainerTranslations(schoolClass);

const updateData = async (): Promise<void> => {
  if (!schoolClass.value?.id) return;
  competenceObservations.value = await fetchCompetenceObservations({ classId: schoolClass.value.id });
  const response = await getReports(schoolClass.value.id);
  reportsNumber.value = response.count;
};

const onSubmit = (): void => {
  emit('continue');
};

const showFinalNoteInput = (): void => {
  isFinalNoteInputVisible.value = true;
};

const updateFinalNote = (note: string): void => {
  finalNoteInternal.value = note;
  emit('update:final-note', note);
};

defineExpose({
  onSubmit,
});

watch(schoolClass, updateData, { immediate: true });
watch(
  finalNote,
  () => {
    finalNoteInternal.value = finalNote.value;
    isFinalNoteInputVisible.value = finalNote.value.length > 0;
  },
  { immediate: true },
);
</script>

<template>
  <form class="grid h-full grid-rows-[min-content_min-content_min-content] px-lg md:px-content" @submit="onSubmit">
    <p v-if="showDescription" class="text-subtitle mb-md">
      {{ tContainer('archiveOverviewDescription', { className: schoolClass?.name }) }}
    </p>

    <div class="flex flex-col gap-md bg-surface-secondary rounded-group">
      <ClassOverview
        :school-class="schoolClass"
        :student-count="students.length"
        :class="[allowFinalNote ? 'pb-none' : '']"
      />
      <div v-if="allowFinalNote" class="px-lg pb-lg pt-0 w-full">
        <button
          v-if="!isFinalNoteInputVisible"
          type="button"
          class="text-tint underline underline-offset-4"
          @click="showFinalNoteInput"
        >
          {{ $t('classes.archiveClassAddNote') }}
        </button>
        <FormTextarea
          v-else
          name="finalNote"
          :rows="5"
          :fluid="true"
          :value="finalNoteInternal"
          :placeholder="$t('classes.archiveClassFinalNotePlaceholder')"
          class="min-h-[128px]"
          @update:value="updateFinalNote"
        />
      </div>
    </div>

    <div class="flex max-md:flex-col gap-lg w-full mt-lg">
      <div
        :class="[
          'bg-surface-secondary rounded-group w-full p-md',
          'flex md:flex-col max-md:items-center max-md:gap-md ',
        ]"
      >
        <AnyIcon :icon-name="EIcon.COMPETENCES" class="size-icon-md text-tint md:mb-sm" />
        <p class="text-title">{{ competenceObservationsAmountFormatted }}</p>
        <p class="text-body text-muted">
          {{ $t('general.competenceObservations', { count: competenceObservations.length }) }}
        </p>
      </div>
      <div
        :class="[
          'bg-surface-secondary rounded-group w-full p-md',
          'flex md:flex-col max-md:items-center max-md:gap-md ',
        ]"
      >
        <AnyIcon :icon-name="EIcon.DOCUMENT_LINES" class="size-icon-md text-tint md:mb-sm" />
        <p class="text-title">{{ reportsNumber }}</p>
        <p class="text-body text-muted">{{ $t('reports.reportCount', reportsNumber) }}</p>
      </div>
    </div>
  </form>
</template>
